import React, { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Info, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  isPushSupported,
  enablePush,
  disablePush,
  pushStatus,
} from "@/lib/push";

// Rileva iOS Safari (iPhone/iPad)
function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}
// Rileva se l'app è aperta come PWA (aggiunta alla Home)
function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
}

/**
 * Pulsante compatto per attivare/disattivare le notifiche push.
 * Su iOS mostra un hint chiaro se non in modalità PWA.
 */
export default function PushEnableButton({ compact = false }) {
  const [supported, setSupported] = useState(false);
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ios, setIos] = useState(false);
  const [pwa, setPwa] = useState(false);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const sup = isPushSupported();
    setSupported(sup);
    setIos(isIOS());
    setPwa(isStandalone());
    if (!sup) return;
    pushStatus().then((s) => setActive(s.server && s.local));
  }, []);

  // Su iOS, se non installato come PWA, il permesso push non funziona
  const iosNeedsPwa = ios && !pwa;

  if (!supported && !ios) return null;

  const onToggle = async () => {
    if (iosNeedsPwa) { setShowHint(true); return; }
    setLoading(true);
    try {
      if (active) {
        await disablePush();
        setActive(false);
        toast.success("Notifiche push disattivate");
      } else {
        await enablePush();
        setActive(true);
        toast.success("Notifiche push attivate su questo dispositivo");
      }
    } catch (e) {
      toast.error(e.message || "Errore attivazione notifiche");
    } finally {
      setLoading(false);
    }
  };

  const Icon = active ? BellRing : Bell;

  if (compact) {
    return (
      <>
        <button
          data-testid="push-toggle-compact"
          onClick={onToggle}
          disabled={loading}
          title={iosNeedsPwa ? "Aggiungi prima l'app alla schermata Home" : (active ? "Notifiche attive — clic per disattivare" : "Attiva notifiche push")}
          className={`w-9 h-9 grid place-items-center rounded-full transition ${
            active ? "bg-[#E2725B]/10 text-[#E2725B]" : iosNeedsPwa ? "text-amber-600 hover:bg-amber-50" : "text-stone-500 hover:bg-stone-100"
          }`}
        >
          <Icon size={16} />
        </button>
        {showHint && <IosHintModal onClose={() => setShowHint(false)}/>}
      </>
    );
  }

  return (
    <>
      <Button
        data-testid="push-toggle"
        variant={active ? "default" : "outline"}
        size="sm"
        onClick={onToggle}
        disabled={loading}
        className={active ? "bg-[#1C352D] hover:bg-[#1C352D]/90 text-white rounded-full" : "rounded-full"}
      >
        {active ? <BellRing size={14} className="mr-1.5" /> : <BellOff size={14} className="mr-1.5" />}
        {active ? "Notifiche attive" : iosNeedsPwa ? "Come attivare notifiche" : "Attiva notifiche"}
      </Button>
      {showHint && <IosHintModal onClose={() => setShowHint(false)}/>}
    </>
  );
}

function IosHintModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Info size={20} className="text-amber-600"/>
            <h3 className="text-lg font-semibold">Notifiche su iPhone</h3>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={18}/></button>
        </div>
        <div className="text-sm text-stone-700 space-y-3">
          <p>Per ricevere le notifiche su iPhone, iOS richiede <b>due passaggi obbligatori</b>:</p>
          <div className="space-y-2 pl-2">
            <div>
              <div className="font-medium text-stone-900">1️⃣ Aggiungi l'app alla schermata Home</div>
              <div className="text-xs text-stone-600 mt-1">
                In Safari, tocca il pulsante <b>Condividi</b> (⬆️ in basso) → <b>"Aggiungi a Home"</b> → <b>Aggiungi</b>.
              </div>
            </div>
            <div>
              <div className="font-medium text-stone-900">2️⃣ Apri l'app dall'icona sulla Home</div>
              <div className="text-xs text-stone-600 mt-1">
                Chiudi Safari, tocca l'icona <b>RDM ClassRoom</b> sulla Home. Solo così iOS abilita le notifiche.
              </div>
            </div>
            <div>
              <div className="font-medium text-stone-900">3️⃣ Fai login e clicca "Attiva notifiche"</div>
              <div className="text-xs text-stone-600 mt-1">
                Quando iOS chiede il permesso, tocca <b>Consenti</b>. Da quel momento riceverai le notifiche degli avvisi.
              </div>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
            <b>Requisito:</b> iOS 16.4 o superiore. Se non hai il pulsante "Aggiungi a Home", aggiorna iOS.
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={onClose} className="bg-[#1C352D] hover:bg-[#264A3E] text-white rounded-full">Ho capito</Button>
        </div>
      </div>
    </div>
  );
}
